/**
 * Replaces Mapbox Satellite imagery with swisstopo's current color orthophoto.
 *
 * SWISSIMAGE Hintergrund (`ch.swisstopo.swissimage`) is the federal geoportal's
 * latest color mosaic, served as Web Mercator XYZ tiles. Native tiles exist
 * through zoom 20; Mapbox overzooms past that. Street labels and Popety
 * overlays stay on the Mapbox style.
 */

const MAPBOX_SATELLITE_ID = 'mapbox.satellite'

export const SWISSIMAGE_CURRENT_TILE_URL =
  'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857/{z}/{x}/{y}.jpeg'

export const SWISSIMAGE_ATTRIBUTION = '© swisstopo'

export const SWISSIMAGE_MAX_ZOOM = 20

export const SWISSIMAGE_TILE_SIZE = 256

const PATCHED = '__swissimageBasemapPatched'
const BOUND = '__swissimageBasemapBound'

export interface RasterSourceLike {
  type?: string
  url?: string
  tiles?: string[]
  tileSize?: number
  maxzoom?: number
  scheme?: string
  attribution?: string
}

export interface MapStyleLike {
  sources?: Record<string, RasterSourceLike>
  layers?: Array<{ id: string; source?: string }>
}

export interface SatelliteMap {
  getStyle: () => MapStyleLike | null | undefined
  getSource: (id: string) => unknown
  getLayer?: (id: string) => unknown
  removeLayer: (id: string) => void
  removeSource: (id: string) => void
  addSource: (id: string, source: RasterSourceLike) => void
  addLayer: (layer: { id: string; source?: string }, beforeId?: string) => void
  on: (event: string, handler: () => void) => void
}

type SetStyle = ((style: unknown, options?: unknown) => unknown) &
  Record<string, unknown>

interface MapboxLibrary {
  Map: {
    prototype: {
      // Method syntax stays bivariant, so Mapbox's narrower setStyle parameters
      // are still assignable. A function property typed with `unknown` is not.
      setStyle(style: unknown, options?: unknown): unknown
    }
  }
}

export const isMapboxSatelliteRasterSource = (
  source: RasterSourceLike | undefined
): boolean => {
  if (!source || source.type !== 'raster') return false
  if (source.url?.includes(MAPBOX_SATELLITE_ID)) return true
  return (source.tiles ?? []).some(
    (tile) =>
      tile.includes(MAPBOX_SATELLITE_ID) ||
      tile.includes('tiles.mapbox.com/v4/mapbox.satellite')
  )
}

export const swissimageRasterSource = (): RasterSourceLike => ({
  type: 'raster',
  tiles: [SWISSIMAGE_CURRENT_TILE_URL],
  tileSize: SWISSIMAGE_TILE_SIZE,
  maxzoom: SWISSIMAGE_MAX_ZOOM,
  scheme: 'xyz',
  attribution: SWISSIMAGE_ATTRIBUTION,
})

export const replaceMapboxSatelliteSources = <T>(style: T): T => {
  if (!style || typeof style !== 'object') return style
  const sources = (style as MapStyleLike).sources
  if (!sources) return style

  let changed = false
  const nextSources: Record<string, RasterSourceLike> = { ...sources }
  for (const [id, source] of Object.entries(sources)) {
    if (!isMapboxSatelliteRasterSource(source)) continue
    nextSources[id] = swissimageRasterSource()
    changed = true
  }

  if (!changed) return style
  return { ...(style as object), sources: nextSources } as T
}

const reinsertLayer = (
  map: SatelliteMap,
  layers: Array<{ id: string; source?: string }>,
  layer: { id: string; source?: string },
  sourceId: string
) => {
  const index = layers.findIndex((candidate) => candidate.id === layer.id)
  const beforeId = layers
    .slice(index + 1)
    .find((candidate) => candidate.source !== sourceId)?.id
  map.addLayer(layer, beforeId)
}

/**
 * Swaps a live style's Mapbox Satellite rasters for SWISSIMAGE.
 * No-op when the style has no Mapbox Satellite source.
 */
export const applySwissimageSatellite = (map: SatelliteMap) => {
  let style: MapStyleLike | null | undefined
  try {
    style = map.getStyle()
  } catch {
    return
  }
  if (!style?.sources) return

  const layers = style.layers ?? []
  for (const [sourceId, source] of Object.entries(style.sources)) {
    if (!isMapboxSatelliteRasterSource(source)) continue
    if (!map.getSource(sourceId)) continue

    const satelliteLayers = layers.filter((layer) => layer.source === sourceId)
    for (const layer of satelliteLayers) {
      if (!map.getLayer || map.getLayer(layer.id)) {
        map.removeLayer(layer.id)
      }
    }
    map.removeSource(sourceId)
    map.addSource(sourceId, swissimageRasterSource())
    for (const layer of satelliteLayers) {
      reinsertLayer(map, layers, layer, sourceId)
    }
  }
}

/**
 * Rewrites satellite style objects before Mapbox loads them, and swaps
 * URL-loaded styles once their sources exist. Idempotent.
 */
export const installSwissimageBasemap = (mapbox: MapboxLibrary) => {
  const prototype = mapbox.Map?.prototype as
    | (MapboxLibrary['Map']['prototype'] & Record<string, unknown>)
    | undefined
  const originalSetStyle = prototype?.setStyle as SetStyle | undefined
  if (!prototype || typeof originalSetStyle !== 'function') return
  if (originalSetStyle[PATCHED]) return

  const setStyle = function (
    this: SatelliteMap & Record<string, unknown>,
    style: unknown,
    options?: unknown
  ) {
    const next =
      style && typeof style === 'object'
        ? replaceMapboxSatelliteSources(style)
        : style
    if (!this[BOUND]) {
      this[BOUND] = true
      this.on('style.load', () => {
        try {
          applySwissimageSatellite(this)
        } catch (error) {
          console.error('Failed to apply SWISSIMAGE satellite basemap', error)
        }
      })
    }
    return originalSetStyle.call(this, next, options)
  } as SetStyle

  setStyle[PATCHED] = true
  prototype.setStyle = setStyle
}
